'use client'

import { SearchIcon, XIcon } from 'lucide-react'
import { useRouter } from 'next/navigation'
import { useEffect, useId, useRef, useState } from 'react'

import { Input } from '@/components/ui/input'
import { buildListingHref } from '@/lib/marketing/pagination'

/**
 * Free-text filter for a marketing listing.
 *
 * IT IS A REAL FORM, and that is not a formality. The catalogue is paginated on
 * the server, so the client only ever holds the twelve cards of the current
 * page — filtering those would search a twelfth of the list and confidently
 * report nothing. The query has to reach the server, which means it has to be
 * in the URL, which means a `<form method="get">` is already the whole feature.
 * The debounce below is a nicety layered on top; with JS off, or before it
 * hydrates, Enter still works.
 *
 * `router.replace`, not `push`: a search is one destination being refined, and
 * pushing per keystroke buries the page the visitor arrived on under a stack of
 * its own intermediate queries — on a listing where Back is how you leave.
 */
export function ListingSearch({
  basePath,
  category,
  defaultValue,
  placeholder = 'Search',
  label,
  resultCount,
  onSearch,
}: {
  basePath: string
  /** Preserved across searches, so a search inside a category stays in it. */
  category?: string
  defaultValue: string
  placeholder?: string
  /** Accessible name for the input. Visually hidden. */
  label: string
  /** Announced to screen readers after the results change. */
  resultCount?: number
  /** Fired once per settled query, not per keystroke. */
  onSearch?: (query: string) => void
}) {
  const router = useRouter()
  const inputId = useId()
  const [value, setValue] = useState(defaultValue)

  // The URL is the source of truth: Back, Forward and a pasted link all change
  // `defaultValue`, and the box has to follow them. Adjusted during render
  // rather than in an effect — React re-renders before committing, so the box
  // never paints the stale query for a frame.
  const [urlQuery, setUrlQuery] = useState(defaultValue)
  if (defaultValue !== urlQuery) {
    setUrlQuery(defaultValue)
    setValue(defaultValue)
  }

  // Held in a ref so an inline `onSearch={q => track(...)}` at the call site —
  // a new function identity every render — does not restart the timer under the
  // visitor's fingers.
  const onSearchRef = useRef(onSearch)
  useEffect(() => {
    onSearchRef.current = onSearch
  })

  // Only typing navigates. Comparing against the URL rather than tracking
  // "did the user type" keeps mount, Back/Forward and the replace() this effect
  // itself performs all quiet, because in every one of those the box and the
  // URL already agree.
  useEffect(() => {
    if (value === defaultValue) return
    const timer = setTimeout(() => {
      onSearchRef.current?.(value)
      router.replace(buildListingHref(basePath, { category, q: value }), {
        scroll: false,
      })
    }, 250)
    return () => clearTimeout(timer)
  }, [basePath, category, defaultValue, router, value])

  return (
    <form
      action={basePath}
      method="get"
      role="search"
      className="mb-6"
      onSubmit={event => {
        // Hydrated: the debounce above already owns navigation, and letting the
        // form submit as well would reload the page and drop `scroll: false`.
        event.preventDefault()
        onSearchRef.current?.(value)
        router.replace(buildListingHref(basePath, { category, q: value }), {
          scroll: false,
        })
      }}
    >
      {category && category !== 'all' && (
        <input type="hidden" name="category" value={category} />
      )}
      <label htmlFor={inputId} className="sr-only">
        {label}
      </label>
      <div className="relative max-w-md">
        <SearchIcon className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
        <Input
          id={inputId}
          name="q"
          type="search"
          value={value}
          onChange={event => setValue(event.target.value)}
          placeholder={placeholder}
          autoComplete="off"
          maxLength={80}
          className="h-11 pr-10 pl-9"
        />
        {value && (
          <button
            type="button"
            onClick={() => setValue('')}
            aria-label="Clear search"
            className="text-muted-foreground hover:text-foreground absolute top-1/2 right-2 -translate-y-1/2 rounded-md p-1.5 transition-colors"
          >
            <XIcon className="size-4" />
          </button>
        )}
      </div>
      {/* The grid changing under a debounce is silent to a screen reader, so the
          count is announced instead. Empty until a query exists, so it does not
          read out the full catalogue size on arrival. */}
      <p aria-live="polite" className="sr-only">
        {defaultValue ? `${resultCount ?? 0} results for ${defaultValue}` : ''}
      </p>
    </form>
  )
}
